/**
 * 动画观察器 - 用于实现滚动触发动画
 */
const observedElements = new Set<Element>();
const pendingAnimations = new Map<Element, number>();
const VISIBILITY_CONFIRM_DELAY_MS = 120;
const TRANSITION_DRIVEN_ANIMATIONS = new Set(['fade-in-up', 'fade-in']);

function isElementVisible(element: HTMLElement) {
  if (!element.isConnected) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }

  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);

  return visibleHeight > 0 && visibleWidth > 0;
}

function clearPendingAnimation(element: Element) {
  const timeoutId = pendingAnimations.get(element);
  if (timeoutId == null) {
    return;
  }

  window.clearTimeout(timeoutId);
  pendingAnimations.delete(element);
}

export class AnimationObserver {
  options: IntersectionObserverInit;
  observer: IntersectionObserver;

  constructor(options: IntersectionObserverInit = {}) {
    this.options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
      ...options
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          if (pendingAnimations.has(element)) {
            return;
          }

          const timeoutId = window.setTimeout(() => {
            pendingAnimations.delete(element);

            if (!isElementVisible(element) || element.classList.contains('animated')) {
              return;
            }

            // 根据元素的data-animation类型应用不同的动画
            const animationType = element.dataset.animation || 'fade-in-up';
            if (!TRANSITION_DRIVEN_ANIMATIONS.has(animationType)) {
              element.classList.add(animationType);
            }
            element.classList.add('animated');
            observedElements.delete(element);

            // 取消观察该元素，因为动画已经触发
            this.observer.unobserve(element);
          }, VISIBILITY_CONFIRM_DELAY_MS);

          pendingAnimations.set(element, timeoutId);
        }
      });
    }, this.options);
  }

  /**
   * 观察单个元素
   * @param element - 要观察的元素
   */
  observe(element: Element) {
    this.observer.observe(element);
  }

  /**
   * 观察多个元素
   * @param elements - 要观察的元素集合
   */
  observeAll(elements: NodeList | Element[]) {
    Array.from(elements).forEach(element => {
      if (element.nodeType === Node.ELEMENT_NODE) {
        this.observer.observe(element as Element);
      }
    });
  }

  /**
   * 停止观察某个元素
   * @param element - 要停止观察的元素
   */
  unobserve(element: Element) {
    clearPendingAnimation(element);
    this.observer.unobserve(element);
  }

  /**
   * 断开观察器
   */
  disconnect() {
    pendingAnimations.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    pendingAnimations.clear();
    this.observer.disconnect();
  }
}

let animationObserver: AnimationObserver | null = null;
let domObserver: MutationObserver | null = null;

function ensureAnimationObserver() {
  if (!animationObserver) {
    animationObserver = new AnimationObserver();
  }
  return animationObserver;
}

function observeAnimatedElement(element: Element) {
  const htmlElement = element as HTMLElement;
  if (
    !htmlElement.hasAttribute('data-animate-on-scroll') ||
    htmlElement.classList.contains('animated') ||
    observedElements.has(htmlElement)
  ) {
    return;
  }

  observedElements.add(htmlElement);
  ensureAnimationObserver().observe(htmlElement);
}

function observeAnimatedTree(root: ParentNode) {
  if (root instanceof Element) {
    observeAnimatedElement(root);
  }

  const nestedElements = root.querySelectorAll?.('[data-animate-on-scroll]');
  nestedElements?.forEach(observeAnimatedElement);
}

function ensureDomObserver() {
  if (domObserver || typeof MutationObserver === 'undefined') {
    return;
  }

  const target = document.body || document.documentElement;
  if (!target) {
    return;
  }

  domObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }
        observeAnimatedTree(node as Element);
      });
    });
  });

  domObserver.observe(target, {
    childList: true,
    subtree: true,
  });
}

/**
 * 初始化所有带有data-animate-on-scroll属性的元素
 */
export function initAnimationObserver() {
  if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') {
    return;
  }

  // 获取所有需要动画的元素
  const animatedElements = document.querySelectorAll('[data-animate-on-scroll]');

  animatedElements.forEach((element) => {
    observeAnimatedElement(element);
  });

  ensureDomObserver();
}
